// ═══════════════════════════════════════════════════════════════════════
// Temps › Mes demandes (agent E2) — écran du salarié : demander une permission, déclarer un arrêt,
// suivre ses demandes. Simple, une action principale : « Demander une permission ».
// Permission : raccourcis Journée · Matin · Après-midi, puis « Autre plage » (D-23). Les bornes du
// matin et de l'après-midi viennent de l'horaire du jour, côté serveur.
// Arrêt : dates et certificat (photo ou fichier), ou « plus tard ». Aucun motif médical demandé.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import axios from 'axios';
import { CalendarClock, Plus, FileHeart, Upload, Ban, Inbox } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre, duree } from './reglagesCommun';
import { ouvrirFichierSecurise } from '../../utils/fichiersSecurises';
// Suivi « prochain acteur » (vue salarié, GET /api/moi/demandes) : qui doit agir et depuis quand.
import { LigneSuivi, useSuiviMesDemandes } from '../../components/suivi/SuiviDemande';

const API = '/api/temps';
export const lireApi = async (url, params) => (await axios.get(`${API}${url}`, { params })).data.data;
export const ecrireApi = async (methode, url, corps) => (await axios({ method: methode, url: `${API}${url}`, data: corps })).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;

const TONS = { EN_ATTENTE: 'info', VALIDEE: 'succes', REFUSEE: 'danger', ANNULEE: 'neutre' };
const TONS_ARRET = { DECLARE: 'alerte', CERTIFICAT_RECU: 'info', VERIFIE: 'succes', A_COMPLETER: 'alerte', REJETE: 'danger', ANNULE: 'neutre' };
const LIBELLES_ARRET = {
  DECLARE: 'Certificat attendu', CERTIFICAT_RECU: 'Certificat en vérification', VERIFIE: 'Certificat vérifié',
  A_COMPLETER: 'À compléter', REJETE: 'Non retenu', ANNULE: 'Annulé',
};
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const RACCOURCIS = [
  { unite: 'JOURNEE', libelle: 'Journée' },
  { unite: 'MATIN', libelle: 'Matin' },
  { unite: 'APRES_MIDI', libelle: 'Après-midi' },
  { unite: 'PLAGE', libelle: 'Autre plage' },
];

function FenetrePermission({ options, onFermer, onTermine }) {
  const [f, setF] = useState({ unite: 'JOURNEE', date: aujourdhui(), date_fin: '', heure_debut: '', heure_fin: '', type_code: '', motif_code: '', motif: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const complet = f.date && f.type_code && f.motif_code && (f.motif_code !== 'AUTRE' || f.motif.trim().length >= 3)
    && (f.unite !== 'PLAGE' || (f.heure_debut && f.heure_fin));
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try {
      const corps = { unite: f.unite, type_code: f.type_code, motif_code: f.motif_code, motif: f.motif || undefined };
      if (f.unite === 'JOURNEE') Object.assign(corps, { date_debut: f.date, date_fin: f.date_fin || f.date });
      else Object.assign(corps, { date: f.date, heure_debut: f.heure_debut || undefined, heure_fin: f.heure_fin || undefined });
      await ecrireApi('post', '/autorisations/permissions', corps);
      onTermine('Demande envoyée à votre responsable.');
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre="Demander une permission" Icone={CalendarClock} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <div role="radiogroup" aria-label="Durée" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {RACCOURCIS.map((r) => (
          <Bouton key={r.unite} petit variante={f.unite === r.unite ? 'primaire' : 'secondaire'} role="radio" aria-checked={f.unite === r.unite}
            onClick={() => setF({ ...f, unite: r.unite })}>{r.libelle}</Bouton>
        ))}
      </div>
      <Champ libelle={f.unite === 'JOURNEE' ? 'Du' : 'Date'} requis><input type="date" className="rh-champ-saisie" value={f.date} onChange={maj('date')} /></Champ>
      {f.unite === 'JOURNEE' && (
        <Champ libelle="Au" aide="Laissez vide pour une seule journée."><input type="date" className="rh-champ-saisie" value={f.date_fin} min={f.date} onChange={maj('date_fin')} /></Champ>
      )}
      {(f.unite === 'MATIN' || f.unite === 'APRES_MIDI') && (
        <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Les heures sont prises sur votre horaire de ce jour-là.</p>
      )}
      {f.unite === 'PLAGE' && (
        <div style={{ display: 'flex', gap: 12 }}>
          <Champ libelle="De" requis><input type="time" className="rh-champ-saisie" value={f.heure_debut} onChange={maj('heure_debut')} /></Champ>
          <Champ libelle="À" requis><input type="time" className="rh-champ-saisie" value={f.heure_fin} onChange={maj('heure_fin')} /></Champ>
        </div>
      )}
      <Champ libelle="Permission" requis>
        <select className="rh-champ-saisie" value={f.type_code} onChange={maj('type_code')}>
          <option value="">Choisir…</option>
          {(options?.types_permission || []).map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
        </select>
      </Champ>
      <Champ libelle="Motif" requis>
        <select className="rh-champ-saisie" value={f.motif_code} onChange={maj('motif_code')}>
          <option value="">Choisir…</option>
          {(options?.motifs_permission || []).map((m) => <option key={m.code} value={m.code}>{m.libelle}</option>)}
        </select>
      </Champ>
      {f.motif_code === 'AUTRE' && (
        <Champ libelle="Précisez" requis><input className="rh-champ-saisie" value={f.motif} onChange={maj('motif')} maxLength={300} /></Champ>
      )}
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Plus} onClick={envoyer} disabled={!complet || envoi}>Envoyer la demande</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Déclaration d'un arrêt, ou envoi du certificat d'un arrêt déjà déclaré (`arret`). */
function FenetreArret({ arret = null, onFermer, onTermine }) {
  const [f, setF] = useState({ date_debut: aujourdhui(), date_fin: aujourdhui() });
  const [fichier, setFichier] = useState(null);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try {
      const fd = new FormData();
      if (!arret) { fd.append('date_debut', f.date_debut); fd.append('date_fin', f.date_fin || f.date_debut); }
      if (fichier) fd.append('certificat', fichier);
      const r = (await axios.post(`${API}/maladie${arret ? `/${arret.id}/certificat` : ''}`, fd)).data.data;
      const d = r.declaration || {};
      const suite = fichier ? ' Certificat en cours de vérification par le service RH.'
        : ` Pensez à envoyer votre certificat${d.echeance_certificat ? ` avant le ${formaterDate(d.echeance_certificat, true)}` : ''}.`;
      onTermine(arret ? 'Certificat envoyé. Le service RH va le vérifier.'
        : `Arrêt reçu.${suite}${d.en_conflit ? ' Une autre absence couvre déjà ces dates : le service RH est prévenu.' : ''}`);
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Fenetre titre={arret ? 'Envoyer le certificat' : 'Déclarer un arrêt'} Icone={FileHeart} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      {!arret && (
        <>
          <p className="rh-form-aide" style={{ margin: '0 0 12px' }}>Aucune information sur votre santé n'est demandée : seulement les dates et le certificat.</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Champ libelle="Du" requis><input type="date" className="rh-champ-saisie" value={f.date_debut} onChange={(e) => setF({ ...f, date_debut: e.target.value })} /></Champ>
            <Champ libelle="Au" requis><input type="date" className="rh-champ-saisie" value={f.date_fin} min={f.date_debut} onChange={(e) => setF({ ...f, date_fin: e.target.value })} /></Champ>
          </div>
        </>
      )}
      <Champ libelle="Certificat" requis={!!arret} aide={arret ? 'Photo ou PDF, 5 Mo au plus.' : 'Photo ou PDF, 5 Mo au plus. Vous pouvez aussi l\'envoyer plus tard.'}>
        <input type="file" className="rh-champ-saisie" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
          onChange={(e) => setFichier(e.target.files?.[0] || null)} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Upload} onClick={envoyer} disabled={envoi || (arret ? !fichier : !f.date_debut)}>
          {arret ? 'Envoyer' : fichier ? 'Déclarer et envoyer' : 'Déclarer (certificat plus tard)'}
        </Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function MesDemandesPage() {
  const [fenetre, setFenetre] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState('');
  const [versionSuivi, setVersionSuivi] = useState(0);
  const suivis = useSuiviMesDemandes(true, versionSuivi);
  const { donnees: options } = useChargement(() => lireApi('/autorisations/options'), []);
  const { donnees: demandes, erreur, recharger } = useChargement(() => lireApi('/autorisations/mes-demandes'), []);
  const { donnees: arrets, erreur: erreurArrets, recharger: rechargerArrets } = useChargement(() => lireApi('/maladie/mes-arrets'), []);

  const terminer = (m) => { setFenetre(null); setMessage(m); setErreurAction(''); recharger(); rechargerArrets(); setVersionSuivi((v) => v + 1); };
  const annulerDemande = async (d) => {
    try { await ecrireApi('patch', `/autorisations/${d.id}/annuler`, {}); terminer('Demande annulée.'); } catch (e) { setErreurAction(messageErreur(e)); }
  };
  const voirCertificat = async (a) => { const m = await ouvrirFichierSecurise(`${API}/maladie/${a.id}/certificat`); if (m) setErreurAction(m); };

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Mes demandes' }]} titre="Mes demandes"
          sousTitre="Permissions, missions, récupérations et arrêts : suivez où en est chaque demande."
          actions={options?.droits?.demander !== false && (
            <>
              <Bouton Icone={FileHeart} onClick={() => setFenetre({ type: 'arret' })}>Déclarer un arrêt</Bouton>
              <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ type: 'permission' })}>Demander une permission</Bouton>
            </>
          )} />
        {(erreur || erreurArrets || erreurAction) && <BandeauInfo ton="danger">{erreur || erreurArrets || erreurAction}</BandeauInfo>}
        {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}

        {(arrets || []).filter((a) => ['DECLARE', 'A_COMPLETER'].includes(a.statut)).map((a) => (
          <BandeauInfo key={a.id} ton="alerte" titre={a.statut === 'A_COMPLETER' ? 'Votre arrêt est à compléter' : 'Certificat attendu'}
            action={<Bouton petit variante="primaire" Icone={Upload} onClick={() => setFenetre({ type: 'certificat', arret: a })}>Envoyer le certificat</Bouton>}>
            Arrêt du {formaterDate(a.date_debut, true)} au {formaterDate(a.date_fin, true)}
            {a.echeance_certificat ? ` — à envoyer avant le ${formaterDate(a.echeance_certificat, true)}` : ''}
            {a.motif_complement ? `. ${a.motif_complement}` : ''}
          </BandeauInfo>
        ))}

        <Carte titre="Mes demandes" Icone={CalendarClock}>
          {!demandes ? <Squelette largeur="60%" /> : demandes.length === 0 ? (
            <EtatVide Icone={Inbox} titre="Aucune demande" texte="Vos permissions, missions et récupérations apparaîtront ici." />
          ) : (
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Demande</th><th>Quand</th><th>Statut</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {demandes.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <span className="rh-cellule-principale">{d.famille === 'PERMISSION' ? d.type_libelle : d.famille_libelle}{d.lieu ? ` — ${d.lieu}` : ''}</span>
                        <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.motif_libelle || ''}{d.motif && d.motif_code === 'AUTRE' ? ` : ${d.motif}` : ''}</span>
                      </td>
                      <td>{d.quand}{d.duree_minutes ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{duree(d.duree_minutes)}</span> : null}</td>
                      <td>
                        <Badge ton={TONS[d.statut] || 'neutre'} point>{d.statut_libelle}</Badge>
                        {d.motif_refus && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.motif_refus}</span>}
                        {d.statut === 'ANNULEE' && d.motif_annulation && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.motif_annulation}</span>}
                        <LigneSuivi suivi={suivis.get(String(d.id))} />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {d.statut === 'EN_ATTENTE' && <Bouton petit variante="discret" Icone={Ban} onClick={() => annulerDemande(d)}>Annuler</Bouton>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>

        <Carte titre="Mes arrêts" Icone={FileHeart}>
          {!arrets ? <Squelette largeur="40%" /> : arrets.length === 0 ? (
            <EtatVide Icone={Inbox} titre="Aucun arrêt déclaré" />
          ) : (
            <div className="rh-conteneur-tableau">
              <table className="rh-tableau">
                <thead><tr><th>Période</th><th>Statut</th><th>Certificat</th></tr></thead>
                <tbody>
                  {arrets.map((a) => (
                    <tr key={a.id}>
                      <td>Du {formaterDate(a.date_debut, true)} au {formaterDate(a.date_fin, true)}</td>
                      <td>
                        <Badge ton={TONS_ARRET[a.statut] || 'neutre'} point>{LIBELLES_ARRET[a.statut] || a.statut}</Badge>
                        {a.motif_rejet && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{a.motif_rejet}</span>}
                        <LigneSuivi suivi={suivis.get(String(a.id))} />
                      </td>
                      <td>{a.certificat?.present
                        ? <Bouton petit variante="lien" onClick={() => voirCertificat(a)}>Voir mon certificat</Bouton>
                        : <span className="rh-cellule-secondaire">Non envoyé</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Carte>

        {fenetre?.type === 'permission' && <FenetrePermission options={options} onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'arret' && <FenetreArret onFermer={() => setFenetre(null)} onTermine={terminer} />}
        {fenetre?.type === 'certificat' && <FenetreArret arret={fenetre.arret} onFermer={() => setFenetre(null)} onTermine={terminer} />}
      </div>
    </Layout>
  );
}
